import { ArrowRight, CalendarDays, Check, Clock3, Dog, GraduationCap, Languages, ShieldCheck, Wrench } from "lucide-react";

export const dogGroomingGuideUrl = "https://docs.google.com/document/d/1ZgDHwlQ4eURrbLs5UscUkLVP_XcxpNJQiB07wNIvljU";

const levelOneTopics = [
  ["Salon safety, sanitation & personal safety", "Safe restraint, cross-contamination prevention, chemical and electrical safety, bite prevention, and emergency procedures."],
  ["Canine behavior & humane handling", "Fear, anxiety, stress, warning signals, handling-sensitive dogs, humane restraint, and when to stop a service."],
  ["Anatomy, skin, coat & breed types", "Grooming-relevant anatomy, coat types, paws, nails, ears, skin and coat observations, breed recognition, and external-parasite awareness."],
  ["Bathing & product selection", "Professional bathing, product selection and dilution, rinsing, face and ear protection, parasite discovery procedures, and coat preparation."],
  ["Brushing, combing & de-shedding", "Tool selection, line brushing, undercoat removal, comb checks, basic mat removal, and humane matting decisions."],
  ["Drying techniques & dryer safety", "Towel, high-velocity, stand, and fluff drying; coat straightening; temperature monitoring; and precautions for sensitive dogs."],
  ["Nail, paw & basic ear care", "Nail and quick anatomy, trimming and grinding, minor nail-bleeding response, paw inspection, and appropriate external ear care."],
  ["Client intake & practical assessment", "Health and behavior history, coat assessment, matting discussion, service authorization, documentation, and professional pet release."],
] as const;

const levelTwoTopics = [
  "Advanced grooming safety, professional salon judgment, first aid, and canine CPR",
  "Professional terminology and recognition of common breeds and coat profiles",
  "Tools, blades, shears, dryers, equipment care, and maintenance",
  "Clipper work, guard-comb techniques, sensitive-area clipping, and pattern setting",
  "Straight, curved, thinning, blending, and chunking shear techniques",
  "Poodle and doodle-type grooming, teddy-bear styling, terrier and schnauzer profiles, spaniel work, drop coats, double coats, and mixed-breed styling",
  "Advanced de-shedding, dematting, carding, hand stripping, and coat-preservation decisions",
  "Finish grooming of faces, feet, ears, tails, topknots, sanitary areas, and final quality control",
  "Client relations, pricing, scheduling, documentation, and grooming-business fundamentals",
  "Complete salon practicum from client intake through checkout, sanitation, and workstation reset",
] as const;

const careerSettings = [
  "Grooming salons and pet-care businesses",
  "Boarding and daycare facilities",
  "Mobile grooming operations",
  "Related pet-care settings that offer grooming",
  "Eventually, your own grooming business",
] as const;

export function DogGroomingProspectGuide() {
  return (
    <section className="dog-guide" aria-labelledby="dog-guide-title">
      <header className="dog-guide-intro">
        <div>
          <p className="eyebrow">Prospective student guide</p>
          <h2 id="dog-guide-title">Build a career working with dogs.</h2>
          <p>
            The 500-hour Professional Dog Grooming Program is primarily hands-on and prepares students for the
            routine pets, services, and decisions seen in a real grooming salon. Students learn through instructor
            demonstrations, supervised practice, correction, and repetition—with two dogs scheduled for each
            student on every training day they attend.
          </p>
        </div>
        <a className="button outline" href={dogGroomingGuideUrl} target="_blank" rel="noreferrer">
          Review the source guide <ArrowRight aria-hidden="true" />
        </a>
      </header>

      <div className="dog-guide-stats" aria-label="Program at a glance">
        <article><Clock3 aria-hidden="true" /><strong>500</strong><span>total training hours</span></article>
        <article><GraduationCap aria-hidden="true" /><strong>~90%</strong><span>hands-on training</span></article>
        <article><Dog aria-hidden="true" /><strong>2 dogs</strong><span>scheduled per training day</span></article>
        <article><CalendarDays aria-hidden="true" /><strong>1–6 days</strong><span>choose how often you attend</span></article>
      </div>

      <section className="dog-guide-objective" aria-labelledby="dog-objective-title">
        <div>
          <p className="eyebrow light">Real-salon preparation</p>
          <h3 id="dog-objective-title">Train for the work groomers see every day.</h3>
        </div>
        <div>
          <p>
            The practical training objective is to prepare graduates to safely and competently groom approximately
            80% of the routine pets and grooming situations commonly brought into a typical salon. Training covers
            bathing, de-shedding, nail and paw care, routine trims, poodle and doodle-type grooming, teddy-bear
            styles, clipper and guard-comb work, scissoring, double and drop coats, common breed-type patterns,
            matting decisions, senior and special-handling pets, client communication, and complete salon workflow.
          </p>
          <p>
            Professional limits are part of the curriculum. Some pets need a highly experienced or specialized
            groomer, veterinary involvement, advanced behavior handling, show-level work, or a modified or referred
            service for safety. The approximately 80% target is a training objective—not a guarantee about every
            graduate or every pet. Competence depends on successful completion of hours, skills, and evaluations and
            continues to grow through professional experience.
          </p>
        </div>
      </section>

      <section className="dog-guide-section" aria-labelledby="dog-curriculum-title">
        <header>
          <p className="eyebrow">What you will learn</p>
          <h3 id="dog-curriculum-title">A two-level professional curriculum.</h3>
          <p>The 120-hour foundation and 380-hour advanced level combine into one 500-hour program.</p>
        </header>
        <div className="dog-curriculum-grid">
          <article>
            <div className="dog-level-heading"><span>Level I</span><strong>120 hours</strong></div>
            <h4>Dog Bathing &amp; Care Technician</h4>
            <p>The foundation every groomer needs before advanced haircutting and styling.</p>
            <ul>{levelOneTopics.map(([title, body]) => <li key={title}><Check aria-hidden="true" /><span><strong>{title}</strong><small>{body}</small></span></li>)}</ul>
          </article>
          <article>
            <div className="dog-level-heading"><span>Level II</span><strong>380 hours</strong></div>
            <h4>Professional Dog Grooming</h4>
            <p>Complete grooming, technical finishing, professional judgment, and salon operations.</p>
            <ul>{levelTwoTopics.map(topic => <li key={topic}><Check aria-hidden="true" /><span>{topic}</span></li>)}</ul>
          </article>
        </div>
        <aside className="dog-practice-note"><Dog aria-hidden="true" /><div><strong>The difference is practice.</strong><p>Safety, humane handling, finish quality, and professional judgment are evaluated throughout repeated supervised performance—not simply by reading about grooming.</p></div></aside>
      </section>

      <section className="dog-guide-schedule" aria-labelledby="dog-schedule-title">
        <div>
          <p className="eyebrow light">A schedule designed for real life</p>
          <h3 id="dog-schedule-title">Train faster—or take your time.</h3>
          <p>
            You are not required to attend the same fixed days every week. Attend one day per week, several days,
            or every available school day, and change your days when work, family, or transportation needs change.
          </p>
        </div>
        <div className="dog-schedule-card">
          <dl>
            <div><dt>Tuesday–Saturday</dt><dd>9:00 AM–3:00 PM</dd></div>
            <div><dt>Sunday</dt><dd>10:00 AM–3:00 PM</dd></div>
            <div><dt>Monday</dt><dd>Closed</dd></div>
            <div><dt>Some training days</dt><dd>May continue to 4:00 PM</dd></div>
          </dl>
          <p><strong>Accelerated estimate:</strong> A student attending every available school day and completing all requirements may finish in approximately 63 school days.</p>
          <p><strong>Part-time option:</strong> A student may attend only one day each week and continue progressing; less frequent attendance means the program will take longer.</p>
          <small>Completion time is not guaranteed. It depends on completed hours, online theory, practical competency check-offs, assignments, attendance, required grooming experience, and final assessments.</small>
        </div>
      </section>

      <section className="dog-guide-section dog-guide-experience" aria-labelledby="dog-experience-title">
        <header>
          <p className="eyebrow">Your training experience</p>
          <h3 id="dog-experience-title">From demonstration to complete salon service.</h3>
        </header>
        <div className="dog-experience-grid">
          <article><Dog aria-hidden="true" /><h4>Two dogs per training day</h4><p>Work with different sizes, coat types, temperaments, grooming needs, and real salon situations. Practice the complete process from assessment and handling through finishing, documentation, and workstation reset.</p></article>
          <article><Languages aria-hidden="true" /><h4>Support for non-native English speakers</h4><p>Visual demonstrations and hands-on practice reduce dependence on lecture alone. Essential English grooming, safety, salon, and client-service terminology is introduced step by step. Perfect English is not required to begin learning the trade.</p></article>
          <article><GraduationCap aria-hidden="true" /><h4>Competency-based progress</h4><p>Move from demonstration to guided and repeated supervised practice, competency check-offs, increasingly independent work, complete grooming services, and final salon simulations. Graduation requires completed hours and demonstrated professional competency.</p></article>
        </div>
      </section>

      <section className="dog-guide-investment" aria-labelledby="dog-investment-title">
        <div>
          <p className="eyebrow light">Know what to expect</p>
          <h3 id="dog-investment-title">Tuition, scholarship, and tools.</h3>
        </div>
        <div className="dog-investment-cards">
          <article><strong>$6,785</strong><span>Program tuition</span><p>Review the enrollment agreement for official tuition and payment terms.</p></article>
          <article><strong>$2,500</strong><span>Completion scholarship</span><p>The application is automatic; the scholarship is awarded after successful course completion.</p></article>
          <article><Wrench aria-hidden="true" /><strong>$600+</strong><span>Estimated initial tools</span><p>Student tools are not included in tuition. Actual cost depends on the brands, quality, and equipment selected; the school provides a recommended list.</p></article>
        </div>
      </section>

      <section className="dog-guide-section dog-guide-fit" aria-labelledby="dog-fit-title">
        <header>
          <p className="eyebrow">Is this program a good fit?</p>
          <h3 id="dog-fit-title">Liking dogs is the beginning—not the whole job.</h3>
          <p>This program may fit someone who prefers practical learning, wants a skill-based career, can stay patient with animals, accepts feedback, and is willing to practice repeatedly.</p>
        </header>
        <div>
          <article>
            <h4>Professional realities</h4>
            <ul>
              <li><Check aria-hidden="true" />Clean and maintain a sanitary work area</li>
              <li><Check aria-hidden="true" />Lift and stand for extended periods</li>
              <li><Check aria-hidden="true" />Work patiently with nervous or difficult pets</li>
              <li><Check aria-hidden="true" />Maintain tools and equipment</li>
              <li><Check aria-hidden="true" />Communicate with clients and keep improving</li>
            </ul>
          </article>
          <article>
            <h4>Career settings</h4>
            <ul>{careerSettings.map(setting => <li key={setting}><Check aria-hidden="true" />{setting}</li>)}</ul>
            <small>Career outcomes depend on skill, experience, local opportunities, and continued professional development.</small>
          </article>
        </div>
      </section>

      <section className="dog-guide-safety" aria-labelledby="dog-safety-title">
        <ShieldCheck aria-hidden="true" />
        <div>
          <p className="eyebrow">Health &amp; safety</p>
          <h3 id="dog-safety-title">Dogs are living animals and can be unpredictable.</h3>
          <p>Students train in canine body language, safe handling and restraint, bite prevention, stress recognition, sanitation, emergency response, and professional stop-service decisions. No school or groomer can guarantee that an animal will never react unexpectedly.</p>
          <p>A current tetanus vaccination is recommended but not required by the school. Ask a healthcare provider about your vaccination history or individual health needs.</p>
        </div>
      </section>

      <footer className="dog-guide-footer">
        <div>
          <p className="eyebrow light">Ready to learn more?</p>
          <h3>Visit the school and see the training environment.</h3>
          <p>Prospective students are encouraged to ask about attendance, tuition, scholarship procedures, tools, and program expectations before enrolling.</p>
        </div>
        <a className="button primary" href="#course-enrollment">Review enrollment options <ArrowRight aria-hidden="true" /></a>
        <small>This page is a promotional summary based on the school’s prospective-student information guide. The school catalog, enrollment agreement, and current school policies contain the official enrollment and academic requirements.</small>
      </footer>
    </section>
  );
}
